import TextLink from '../../../../components/common/TextLink';
import { CarAllocation } from '../../../../types/carAllocation';
import styles from './styles.module.css';
import WorkCategoryTag from '../../../../components/common/WorkCategoryTag';
import CollectionConstraintTypeTag from '../../../../components/common/CollectionConstraintTypeTag';
import PolicyRequired from '../../../../components/common/PolicyRequired';
import { getNumberString } from '../../../../services/getNumberString';
import { formatTime } from '../../../../services/formatTime';
import { useSaveOrigin } from '../../useSaveOrigin';

/** 廃棄品目の最大表示数 (この数以上の品目は省略して表示される) */
const maxWasteItems = 3;

type Props = {
  allocation: CarAllocation;
  /** 廃棄品目を非表示にする */
  compact?: boolean;
  /** 作業カテゴリ、指定時間、定期/スポット、作業推定時間、廃棄品目、詳細を見るを非表示にする */
  minimum?: boolean;
  /** 作業カテゴリ、指定時間、作業推定時間、詳細を見るを非表示にする */
  shortened?: boolean;
};

function AllocatedTask({
  allocation: {
    contractId,
    specificTimeStartAt,
    specificTimeEndAt,
    workCategory,
    taskEstimatedTime,
    wasteItems,
    companyName,
    companyAddress,
    collectionConstraintType,
  },
  compact,
  minimum,
  shortened,
}: Props) {
  const linkQuery = useSaveOrigin();
  return (
    <div className={styles.item}>
      <div className={styles.title}>
        <span className={styles.companyName}>{companyName}</span>
        {!minimum && !shortened && (
          <CollectionConstraintTypeTag type={collectionConstraintType} />
        )}
      </div>
      <div>
        <div className={styles.companyAddress}>{companyAddress}</div>
        {!minimum && !shortened && (
          <SpecificTime
            start={specificTimeStartAt ?? undefined}
            end={specificTimeEndAt ?? undefined}
          />
        )}
        {!minimum && (
          <div>
            <WorkCategoryTag value={workCategory} />{' '}
            <TaskEstimatedTime minutes={taskEstimatedTime} />
          </div>
        )}
        {!compact &&
          !minimum &&
          wasteItems
            .slice(0, maxWasteItems)
            .map((item, index) => (
              <WasteItem
                key={item.id}
                item={item}
                ellipsis={
                  index === maxWasteItems - 1 &&
                  wasteItems.length > maxWasteItems
                }
              />
            ))}
      </div>
      {!minimum && !shortened && (
        <PolicyRequired policy={18}>
          <div className={styles.link}>
            <TextLink to={`/contracts/edit/${contractId}${linkQuery}`}>
              詳細を見る
            </TextLink>
          </div>
        </PolicyRequired>
      )}
    </div>
  );
}

function SpecificTime({ start, end }: { start?: string; end?: string }) {
  // start, end が両方ともなければ何も表示しない
  if (!(start || end)) return null;
  return (
    <div>
      <span className={styles.specificTimeText}>
        {start ? formatTime(start) : start}-{end ? formatTime(end) : end}
      </span>{' '}
      <span className={styles.specificTimeIcon}>指定</span>
    </div>
  );
}

function TaskEstimatedTime({ minutes }: { minutes: number }) {
  // n 分を `0:00` 形式に変換する
  const h = String(Math.floor(minutes / 60));
  const m = String(Math.floor(minutes) % 60).padStart(2, '0');
  const s = `${h}:${m}`;

  return <span>{s}</span>;
}

function WasteItem({
  item: { name, collectedAmount },
  ellipsis,
}: {
  item: CarAllocation['wasteItems'][number];
  ellipsis?: boolean;
}) {
  return (
    <div>
      <span>{name}</span>
      {collectedAmount !== null && (
        <>
          <span className={styles.separationBar}>|</span>
          <span>{`${collectedAmount > 0 ? '+' : ''}${getNumberString(
            collectedAmount
          )}㎥`}</span>
        </>
      )}
      {ellipsis && <span>…</span>}
    </div>
  );
}

export default AllocatedTask;
